import React, { useEffect, useState } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import NInput from '../../components/inputs/NInput';
import NButton from '../../components/inputs/NButton';
import { addRolesValidations } from '../../validations/validation';
import useApplicationAPI from '../../hooks/network/useApplicationAPI';
import NDropdown from '../../components/inputs/NDropdown';
import useRoleAPI from '../../hooks/network/useRoleAPI';
import { errorToaster, successToaster } from '../../components/toaster/Toaster';
import { RouteEnums } from '../../Routes/RoutesConstant';
import { ResponseMessage } from '../../constants/Constants';
import { useFormHook } from '../../hooks/custom-hooks/useFormHook';

const formInitialValues = {
  roleName: '',
  appAccess: [],
  notes: '',
};

function AddRole() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { isLoading, getRole, addRole, updateRole } = useRoleAPI();
  const { getApplications } = useApplicationAPI();
  const { formRef, formHeight } = useFormHook();

  const [formValues, setFormValues] = useState(formInitialValues);
  const [applicationOptions, setApplicationOptions] = useState();

  useEffect(() => {
    if (roleId) {
      fetchRolesDetails();
    }
    fetchApplications();
  }, [roleId]);

  const fetchRolesDetails = async () => {
    try {
      const response = await getRole(roleId);
      setFormValues(response);
    } catch {}
  };

  const fetchApplications = async () => {
    try {
      const response = await getApplications(roleId);
      setApplicationOptions(response);
    } catch {}
  };

  const handleFormSubmit = async (values, formikBag) => {
    try {
      if (roleId) {
        const response = await updateRole(roleId, values);
        if (response?.success) {
          successToaster(response?.message || 'Role Updated');
          navigate(RouteEnums.USER_ROLE);
        } else errorToaster(response?.message || response?.error || ResponseMessage.ERROR_DEFAULT);
      } else {
        const response = await addRole(values);
        if (response?.success) {
          formikBag.resetForm();
          setFormValues(formInitialValues);
          successToaster(response?.message || 'New Role Added');
          navigate(RouteEnums.USER_ROLE);
        } else errorToaster(response?.message || response?.error || ResponseMessage.ERROR_DEFAULT);
      }
    } catch {}
  };

  return (
    <>
      <div className='nmobile-form-blur' />
      <div
        style={{
          height:
            window.innerWidth < 1180
              ? `min(calc(100vh - 150px), calc(100vh - ${formHeight}px))`
              : '100%',
        }}
        className='nform-wrapper'
      >
        <div className='nform-container'>
          {/* Mobile Header Heading */}
          <div className='nmobile-title'>
            <h2 className='app-heading h2-style'>{roleId ? 'Edit' : 'Add'} Role</h2>
            <div onClick={() => navigate(RouteEnums.USER_ROLE)}>
              <CloseIcon />
            </div>
          </div>

          <div className='nform'>
            <div ref={formRef}>
              <Formik
                initialValues={formValues}
                validationSchema={addRolesValidations}
                onSubmit={(values, formikBag) => {
                  handleFormSubmit(values, formikBag);
                }}
                enableReinitialize
              >
                {({ touched, values, errors, handleSubmit, handleChange, handleBlur }) => (
                  <FormikForm className='' autoComplete='off' onSubmit={handleSubmit}>
                    {/* Desktop buttons */}
                    <div className='ntitle-nAction-button'>
                      <h2 className='app-heading h2-style'>{roleId ? 'Edit' : 'Create'} Role</h2>
                      <div className='naction-btns'>
                        <div>
                          <NButton
                            variant='Noutline'
                            onClick={() => navigate(RouteEnums.USER_ROLE)}
                          >
                            Cancel
                          </NButton>
                        </div>
                        <div>
                          <NButton
                            isLoading={isLoading}
                            onClick={handleSubmit}
                            variant='Ncontained'
                          >
                            {roleId ? 'Update' : 'Save'} Role
                          </NButton>
                        </div>
                      </div>
                    </div>

                    {/* Common form */}
                    <div className='nform-fields'>
                      <div className='w-full'>
                        <NInput
                          required
                          name='roleName'
                          value={values.roleName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.roleName && touched.roleName}
                          helperText={errors.roleName && touched.roleName ? errors.roleName : ''}
                          className='w-full'
                          label='Role Name'
                          placeholder='Enter Role Name'
                          type='text'
                        />
                      </div>
                      <div>
                        <NDropdown
                          required
                          multiple
                          label='Application*'
                          name='appAccess'
                          value={values.appAccess}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.appAccess && touched.appAccess}
                          helperText={errors.appAccess && touched.appAccess ? errors.appAccess : ''}
                          renderValue={(selected) =>
                            selected?.length ? `${selected?.length} Selected` : ''
                          }
                          className='w-full'
                          type='text'
                          options={applicationOptions?.map((o) => ({
                            label: o?.name,
                            value: o?.code,
                          }))}
                        />
                      </div>
                      <div className='w-full'>
                        <NInput
                          required
                          name='notes'
                          value={values.notes}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.notes && touched.notes}
                          helperText={errors.notes && touched.notes ? errors.notes : ''}
                          className='w-full'
                          label='Description'
                          placeholder='Enter Description'
                          type='text'
                          multiline
                          rows={7}
                        />
                      </div>
                    </div>

                    {/* Mobile Buttons */}
                    <div className='nmobile-action-btn'>
                      <div>
                        <NButton isLoading={isLoading} onClick={handleSubmit} variant='Ncontained'>
                          {roleId ? 'Update' : 'Save'} Role
                        </NButton>
                      </div>
                    </div>
                  </FormikForm>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddRole;
